import React, { useState, useEffect, useRef } from "react";
import HamburgerIcon from "../../../assets/icons/Hamburger";
import { UserRole } from "../../../utils/api/endpoints/user/credentials"; // Ensure correct import

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  role: UserRole;
  onToggle?: (expanded: boolean) => void;
  allSections?: { name: string; content: string }[];
  isOpen?: boolean; // New prop for external control
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, setActiveSection, role, onToggle, allSections, isOpen }) => {
  const [expanded, setExpanded] = useState(isOpen || false);
  const [highlightHeight, setHighlightHeight] = useState(0);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  // Sync internal state with external prop
  useEffect(() => {
    if (isOpen !== undefined) {
      setExpanded(isOpen);
    }
  }, [isOpen]);


  // Measure the ProgramHighlight container height
  useEffect(() => {
    const updateHeight = () => {
      const highlightContainer = document.querySelector('.program-highlight-container');
      if (highlightContainer) {
        setHighlightHeight(highlightContainer.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    // Also update when content changes
    const observer = new MutationObserver(updateHeight);
    const highlightContainer = document.querySelector('.program-highlight-container');
    if (highlightContainer) {
      observer.observe(highlightContainer, { childList: true, subtree: true, attributes: true });
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      observer.disconnect();
    };
  }, []);

  // Close on outside click, but NOT on interactive elements
  useEffect(() => {
    function handlePointerDown(event: Event) {
      if (!expanded) return;
      const target = event.target as Element | null;
      if (!target) return;

      // Don't close if clicking inside the hamburger menu
      if (sidebarRef.current && sidebarRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on interactive elements
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      const isTab = target.closest('[role="tab"]') || target.closest('.tab') || target.textContent?.includes('Program Details') || target.textContent?.includes('Scope Description') || target.textContent?.includes('Other');
      const isButton = target.tagName === 'BUTTON' || target.closest('button');
      const isClickable = target.closest('a') || target.closest('[onclick]') || target.closest('[role="button"]');

      // If clicking on any interactive element, don't close the menu
      if (isInputField || isTab || isButton || isClickable) {
        return;
      }

      // Close only on non-interactive outside clicks
      setExpanded(false);
      onToggle && onToggle(false);
    }
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown as any, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown as any, true);
    };
  }, [expanded, onToggle]);

  const sections = (allSections ? allSections.map(s => s.name) : [
    "Overview",
    "Scope",
    "Documentation",
    "Scheduled Runs",
    "Discovery",
    "Credential Vault",
    "Attachments",
    "Test Checklists",
    "Recent Activity",
    "Reports",
    ...([UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role) ? ["Management"] : []),
    "Terms & Conditions"
  ]).filter((section) => {
    // Filter sections based on role
    if (section === "Management") {
      return [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role);
    }
    if (section === "Discovery") {
      return [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.RESEARCHER, UserRole.BUSINESS].includes(role);
    }
    if (section === "Credential Vault") {
      return [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.RESEARCHER].includes(role);
    }
    if (section === "Recent Activity" || section === "Reports") {
      return ![UserRole.BUSINESS_MANAGER, UserRole.ADMIN_MANAGER].includes(role);
    }
    return true; // Show all other sections
  });

  return (
    <div
      ref={sidebarRef}
      className={`transition-all relative -ms-3 ${expanded ? "bg-white shadow-md w-64 p-4 me-4 rounded-lg" : "bg-transparent me-4 w-auto p-0 shadow-none"
        }`}
      style={expanded && highlightHeight > 0 ? { height: `${highlightHeight}px` } : {}}
    >
      {!expanded && (
        <button
          className="bg-white p-3 rounded-full shadow-lg relative z-10"
          onClick={(e) => {
            e.stopPropagation();
            const next = !expanded;
            setExpanded(next);
            onToggle && onToggle(next);
          }}
        >
          <HamburgerIcon className="w-6 h-6" />
        </button>
      )}

      {expanded && (
        <ul className="space-y-1 overflow-y-auto max-h-full">
          {sections.map((section) => (
            <li key={section}>
              <button
                onClick={() => setActiveSection(section)}
                className={`block w-full text-left px-4 py-3 rounded-lg transition ${activeSection === section
                  ? "bg-[#010D2C] text-white"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                {section}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Sidebar;
